import { useState, useEffect } from 'react';
import { useAppContext } from '../../../../shared/context/AppContext';
import { useAnalytics } from '../../../../shared/hooks/useAnalytics';
import { EVENTS } from '../../../../shared/services/analyticsEvents';
import { getUserId } from '../../../../shared/domain/services/userService';
import * as upvoteController from '../controllers/upvoteController';
import * as downvoteController from '../controllers/downvoteController';
import * as favoriteController from '../controllers/favoriteController';
import * as promptLibraryService from '../../data/services/promptLibraryService';

/**
 * Custom hook for managing prompt interactions (upvote, downvote, favorite)
 * @param {Object} prompt - The prompt object
 * @param {Function} onUpdate - Optional callback when prompt is updated (for voting)
 * @param {Function} onFavoriteToggle - Optional callback when favorite is toggled (prompt, isFavorited)
 * @returns {Object} Interaction state and handlers
 */
export const usePromptInteractions = (prompt, onUpdate, onFavoriteToggle) => {
  const { user, organization } = useAppContext();
  const { trackEvent } = useAnalytics();
  const [isUpvoted, setIsUpvoted] = useState(false);
  const [isDownvoted, setIsDownvoted] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [upvotes, setUpvotes] = useState(prompt?.upvotes || 0);
  const [downvotes, setDownvotes] = useState(prompt?.downvotes || 0);

  // Load initial interaction state
  useEffect(() => {
    const loadInteractions = async () => {
      if (user && organization && prompt?.id) {
        const userId = getUserId(user);
        if (!userId) return;
        try {
          const interactions = await promptLibraryService.getUserPromptInteractions(
            userId,
            organization.id,
            prompt.id
          );
          setIsUpvoted(interactions.upvoted);
          setIsDownvoted(interactions.downvoted);
          setIsFavorited(interactions.favorited);
        } catch (error) {
          console.error('Error loading interactions:', error);
        }
      }
    };
    loadInteractions();
  }, [user, organization, prompt?.id]);

  // Update vote counts when prompt changes (numbers only, not interaction states)
  useEffect(() => {
    if (prompt) {
      setUpvotes(prompt.upvotes || 0);
      setDownvotes(prompt.downvotes || 0);
      // Interaction states (isUpvoted, isDownvoted, isFavorited) are NOT synced here
      // They are only set by: 1) Initial load from DB, 2) Handler functions after API calls
      // This keeps votes and favorites independent
    }
  }, [prompt]);

  const handleUpvote = async (e) => {
    if (!user || !organization || !prompt?.id) return;

    const userId = getUserId(user);
    if (!userId) return;

    try {
      const result = await upvoteController.toggleUpvote(prompt.id, userId, organization.id);
      setIsUpvoted(result.isUpvoted);
      setIsDownvoted(result.isDownvoted);
      setUpvotes(result.prompt.upvotes);
      setDownvotes(result.prompt.downvotes);
      if (onUpdate) {
        onUpdate(result.prompt);
      }
      // Track upvote event
      trackEvent(EVENTS.PROMPT_UPVOTED, {
        promptId: prompt.id,
        promptTitle: prompt.title,
        isGlobal: prompt.isGlobal || false,
      });
    } catch (error) {
      console.error('Error upvoting:', error);
    }
  };

  const handleDownvote = async (e) => {
    if (!user || !organization || !prompt?.id) return;

    const userId = getUserId(user);
    if (!userId) return;

    try {
      const result = await downvoteController.toggleDownvote(prompt.id, userId, organization.id);
      setIsUpvoted(result.isUpvoted);
      setIsDownvoted(result.isDownvoted);
      setUpvotes(result.prompt.upvotes);
      setDownvotes(result.prompt.downvotes);
      if (onUpdate) {
        onUpdate(result.prompt);
      }
      // Track downvote event
      trackEvent(EVENTS.PROMPT_DOWNVOTED, {
        promptId: prompt.id,
        promptTitle: prompt.title,
        isGlobal: prompt.isGlobal || false,
      });
    } catch (error) {
      console.error('Error downvoting:', error);
    }
  };

  const handleFavorite = async (e) => {
    if (!user || !organization || !prompt?.id) return;

    const userId = getUserId(user);
    if (!userId) return;

    try {
      const result = await favoriteController.toggleFavorite(prompt.id, userId, organization.id);
      setIsFavorited(result.isFavorited);
      if (onFavoriteToggle && result.prompt) {
        // Pass both updated prompt and favorite status
        onFavoriteToggle(result.prompt, result.isFavorited);
      } else if (onUpdate && result.prompt) {
        // Fallback to onUpdate if onFavoriteToggle not provided
        onUpdate(result.prompt);
      }
      // Track favorite/unfavorite event
      trackEvent(result.isFavorited ? EVENTS.PROMPT_FAVORITED : EVENTS.PROMPT_UNFAVORITED, {
        promptId: prompt.id,
        promptTitle: prompt.title,
        isGlobal: prompt.isGlobal || false,
      });
    } catch (error) {
      console.error('Error favoriting:', error);
    }
  };

  return {
    isUpvoted,
    isDownvoted,
    isFavorited,
    upvotes,
    downvotes,
    handleUpvote,
    handleDownvote,
    handleFavorite,
  };
};

